import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_FACILITY_ID,
  FACILITY_STORAGE_KEY,
  FACILITIES,
  getFacility,
  isValidFacilityId,
  resolveFacilityId,
  SP_THEME
} from '../utils/facilities';
import { applyFacilityCssVars } from '../styles/facilityThemes';

const FacilityContext = createContext(null);

const readStoredFacility = () => {
  try {
    const raw = localStorage.getItem(FACILITY_STORAGE_KEY);
    if (raw && isValidFacilityId(raw)) return raw;
  } catch (_) {
    /* ignore */
  }
  return DEFAULT_FACILITY_ID;
};

export const FacilityProvider = ({ children }) => {
  const [facilityId, setFacilityIdState] = useState(readStoredFacility);

  useEffect(() => {
    applyFacilityCssVars(facilityId);
  }, [facilityId]);

  const setFacilityId = useCallback((next) => {
    const id = resolveFacilityId(next);
    setFacilityIdState(id);
    try {
      localStorage.setItem(FACILITY_STORAGE_KEY, id);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const facility = useMemo(() => getFacility(facilityId), [facilityId]);
  const isStrawberryPatch = facilityId === 'strawberryPatch';

  const value = useMemo(
    () => ({
      facilityId,
      facility,
      facilities: FACILITIES,
      setFacilityId,
      isStrawberryPatch,
      spTheme: SP_THEME
    }),
    [facilityId, facility, setFacilityId, isStrawberryPatch]
  );

  return (
    <FacilityContext.Provider value={value}>{children}</FacilityContext.Provider>
  );
};

export const useFacility = () => {
  const ctx = useContext(FacilityContext);
  if (!ctx) {
    throw new Error('useFacility must be used within FacilityProvider');
  }
  return ctx;
};
